import React, { useRef } from 'react';
import {
  ArrowDropDownIcon,
  ArrowDropUpIcon,
  DateRangeIcon,
} from 'assets/icons';
import Calendar from 'react-calendar';
import {
  Popper,
  ClickAwayListener,
  ThemeProvider,
  Stack,
  Box,
} from '@mui/material';
import { grey } from 'assets/colors/colors';
import { formatDate } from 'utils/dateUtils';
import MuiButton from 'components/__v5__/Button/MuiButton';
import datePickerTheme from './Theme/datePickerTheme';
import { DatePickerWithPopperProps } from './type/datePickerType';
import useDatePickerHooks from './hooks/useDatePickerHooks';
import { errorStyles } from './style/style';

const iconStyle = {
  justifyContent: 'flex-end',
  display: 'flex',
  fontSize: '24px',
};
const DatePickerWithPopper: React.FC<DatePickerWithPopperProps> = ({
  startDate,
  endDate,
  dateChangeHandler = () => {},
  placeholder = '',
  disabled = false,
  minDate,
  maxDate,
  selectRange,
  showStaticDateRange = false,
  staticDateRangeOptions = [],
  defaultStaticRange,
  hasErrors,
  styleForOuterElement,
  setDateRange,
  calendarHeight = '38px',
  showDateWithDay = false,
  setStartDateToNull = false,
  ...rest
}) => {
  const anchorElRef = useRef<HTMLDivElement>(null);

  const {
    showCalendar,
    date,
    toggleCalendar,
    handleDateChange,
    handleClickAway,
    handleActive,
    onStaticDateRangeChange,
  } = useDatePickerHooks({
    startDate,
    endDate,
    dateChangeHandler,
    defaultStaticRange,
    disabled,
    setDateRange,
    selectRange,
    setStartDateToNull,
  });

  return (
    <ThemeProvider theme={datePickerTheme}>
      <div
        className={`datepicker ${disabled ? 'disabled' : ''}`}
        style={{
          outline: 'none',
          backgroundColor: disabled ? `${grey.g200}` : undefined,
          ...(hasErrors && errorStyles),
          ...styleForOuterElement,
        }}
      >
        <Stack
          spacing={0.5}
          direction="row"
          height="38px"
          alignItems="center"
          ref={anchorElRef}
          className="selected-date"
          onClick={toggleCalendar}
          sx={{
            padding: '0 8px!important',
            height: `${calendarHeight} !important`,
          }}
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center">
            <DateRangeIcon sx={{ mr: 1 }} />
            <div className="date">
              {startDate && (
                <span className="start-date">
                  {showDateWithDay
                    ? formatDate(startDate, 'EEE dd/MM/yyyy')
                    : formatDate(startDate, 'dd/MM/yyyy')}
                </span>
              )}
              {endDate && (
                <span className="end-date">
                  {showDateWithDay
                    ? formatDate(endDate, 'EEE dd/MM/yyyy')
                    : formatDate(endDate, 'dd/MM/yyyy')}
                </span>
              )}
              {!startDate && !endDate && (
                <span className="placeholder">
                  {placeholder || 'Select Date'}
                </span>
              )}
            </div>
          </Box>
          {showCalendar ? (
            <ArrowDropUpIcon sx={iconStyle} />
          ) : (
            <ArrowDropDownIcon sx={iconStyle} />
          )}
        </Stack>

        <Popper
          open={showCalendar}
          anchorEl={anchorElRef.current}
          placement="bottom-start"
        >
          <ClickAwayListener onClickAway={handleClickAway}>
            <>
              {showStaticDateRange && (
                <Stack justifyContent="center" gap={0.5}>
                  {staticDateRangeOptions.map((option) => (
                    <MuiButton
                      type="button"
                      key={option.value}
                      onKeyUp={() => { }}
                      onClick={() => onStaticDateRangeChange(option.value)}
                      style={handleActive(option?.value)}
                    >
                      {option.label}
                    </MuiButton>
                  ))}
                </Stack>
              )}
              <Calendar
                onChange={handleDateChange}
                value={date}
                selectRange={selectRange}
                calendarType="US"
                minDate={minDate}
                maxDate={maxDate}
                {...rest}
              />
            </>
          </ClickAwayListener>
        </Popper>
      </div>
    </ThemeProvider>
  );
};

export default DatePickerWithPopper;
